<template>
    <Modal :title="$t('labels.import')" ref="modal">
        <NavTab class="nav-tabs-bottom nav-justified mb-0">
            <NavTabItem @click="selectTab('api')" href="#api" ref="apiTab">{{ $t("labels.api") }}</NavTabItem>
            <NavTabItem @click="selectTab('file')" href="#file">{{ $t("labels.file") }}</NavTabItem>
            <template #content>
                <NavTabPane id="api">
                    <div class="modal-body pb-0" v-if="!apiServices.includes(integration.service) || !integration.api_credentials?.is_active">
                        <Alert type="info" v-if="!apiServices.includes(integration.service)">
                            <span class="font-weight-semibold mr-2">{{ translate(`alerts.info`) }}</span>
                            {{ $t('alerts.notSupportIntegrationType', {
                                integrationName: this.integration.service.charAt(0).toUpperCase() + this.integration.service.slice(1),
                                integrationType: $t("labels.api")
                            }) }}
                        </Alert>
                    </div>
                    <div class="modal-body" v-else-if="integration.service === IntegrationServiceEnum.WOOCOMMERCE">
                        <InputGroup>
                            <template #prepend>
                                <CheckBox v-model="hasWoocommerceWpmlPlugin" v-if="integration.service === IntegrationServiceEnum.WOOCOMMERCE"/>
                            </template>
                            {{ $t('labels.has_woocommerce_wpml_plugin') }}
                        </InputGroup>
                    </div>
                    <div class="modal-body" v-else-if="integration.service === IntegrationServiceEnum.ROZETKA">
                        <InputGroup>
                            <CheckBox v-model="onlyActive"/>
                            <template #append>
                                {{ $t('labels.only_active_products') }}
                            </template>
                        </InputGroup>
                    </div>
                </NavTabPane>
                <NavTabPane id="file">
                    <div class="modal-body pb-0">
                        <Alert type="info" v-if="!fileServices.includes(integration.service)">
                            <span class="font-weight-semibold mr-2">{{ translate(`alerts.info`) }}</span>
                            {{ $t('alerts.notSupportIntegrationType', {
                                integrationName: integration.service.charAt(0).toUpperCase() + integration.service.slice(1),
                                integrationType: $t("labels.file")
                            }) }}
                        </Alert>
                        <File ref="ImportFile" class="mb-2" v-else/>
                    </div>
                </NavTabPane>
            </template>
        </NavTab>
        <div class="modal-body">
            <InputGroup>
                <CheckBox v-model="deleteMissing"/>
                <template #append>
                    {{ $t('labels.delete_missing_products') }}
                </template>
            </InputGroup>
        </div>
        <div class="card-footer d-flex justify-content-end">
            <Button size="sm" color="indigo" :disabled="disableButton" @click="createImport">
                {{ $t("buttons.start") }}
            </Button>
        </div>
    </Modal>
</template>
<script>
import Modal from "../../../../components/new/modal/AppModal.vue";
import Alert from "../../../../components/AlertComponent.vue";
import Button from "../../../../components/new/button/AppButton.vue";
import NavTab from "../../../components/tabs/NavTab.vue";
import NavTabItem from "../../../components/tabs/NavTabItem.vue";
import NavTabPane from "../../../components/tabs/NavTabPane.vue";
import File from "../../../../components/form/FileComponent.vue";
import CheckBox from "../../../../components/new/form/checkbox/AppCheckbox.vue";
import InputGroup from "../../../../components/new/form/InputGroup.vue";

import { httpClient } from "../../../../services/HttpClient";
import { IntegrationServiceEnum } from "../../../enums/IntegrationServiceEnum";

export default {
    name: "ImportProductModal",
    components: {
        Modal, Alert, Button, NavTab, NavTabItem, NavTabPane, File, CheckBox, InputGroup
    },
    props: {
        integration: {
            type: Object,
            required: true
        },
    },
    emits: ["fetch:table"],
    data() {
        return {
            IntegrationServiceEnum,
            tabHref: null,
            apiServices: [
                IntegrationServiceEnum.PROM,
                IntegrationServiceEnum.KASTA,
                IntegrationServiceEnum.HOROSHOP,
                IntegrationServiceEnum.SHOP_EXPRESS,
                IntegrationServiceEnum.ROZETKA,
                IntegrationServiceEnum.WOOCOMMERCE,
                IntegrationServiceEnum.MAUDAU
            ],
            fileServices: [
                IntegrationServiceEnum.PROM,
                IntegrationServiceEnum.ROZETKA,
                IntegrationServiceEnum.HOROSHOP,
                IntegrationServiceEnum.EPICENTR,
                IntegrationServiceEnum.SHOPIFY,
                IntegrationServiceEnum.ALLO,
                IntegrationServiceEnum.EVA,
            ],
            hasWoocommerceWpmlPlugin: this.integration.product_settings?.has_woocommerce_wpml_plugin ?? false,
            onlyActive: true,
            deleteMissing: false,
            importStarted: false
        }
    },
    methods: {
        show() {
            this.$refs.modal.show();
            this.$nextTick(() => {
                this.$refs.apiTab.click()
            })
        },
        async createImport() {
            this.importStarted = true;
            let request = {
                delete_missing: this.deleteMissing
            };

            if (this.tabHref === "api" && this.integration.service === IntegrationServiceEnum.ROZETKA) {
                request.only_active = this.onlyActive;
            }

            if (this.integration.service === IntegrationServiceEnum.WOOCOMMERCE) {
                await httpClient.patch(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}`, {
                    product_settings: {
                        has_woocommerce_wpml_plugin: this.hasWoocommerceWpmlPlugin
                    }
                })
            }

            if (this.tabHref === "file") {
                let formData = new FormData();

                formData.append('file', this.$refs.ImportFile.file);
                formData.append('type', 'integration-import');

                await httpClient.post(`/file/files`, formData).then((response) => {
                    request.file_uuid = response.data.data.uuid
                })
            }

            await httpClient.post(`/integrations/${this.$route.params.integrationUuid}/imports`, request)
            .then(() => {
                this.$refs.modal.hide()
                this.$emit("fetch:table")
                this.importStarted = false;
                this.tabHref = null;
            });
        },
        selectTab(tabHref) {
            this.tabHref = tabHref
        }
    },
    computed: {
        disableButton() {
            if (this.importStarted) {
                return true;
            }

            if (this.tabHref === "api") {
                return !this.apiServices.includes(this.integration.service) || !this.integration.api_credentials?.is_active;
            }

            if (this.tabHref === "file") {
                return !this.fileServices.includes(this.integration.service) || this.$refs.ImportFile.file === null;
            }

            return false;
        }
    },
}
</script>
